/* eslint-disable max-lines */
import { ArrowDropDown, ArrowRight, Download, Downloading, Settings } from '@mui/icons-material'
import { Box, IconButton, PaginationItem, Stack, styled, SxProps, Theme, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import {
  gridClasses,
  GridCallbackDetails,
  GridCellParams,
  GridColumnOrderChangeParams,
  GridColumns,
  GridColumnVisibilityModel,
  GridEditMode,
  GridEditRowsModel,
  GridEventListener,
  GridInitialState,
  GridInputSelectionModel,
  GridRowClassNameParams,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowId,
  GridRowOrderChangeParams,
  GridRowParams,
  GridSelectionModel,
  GridSortModel,
  GridToolbar,
  GridValidRowModel,
} from '@mui/x-data-grid-pro'
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro'
import React, { Fragment, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogContextType } from 'src/enums'
import { QueryKeyT } from '../../api/reactQuery'
import { QuickFilterBounceTime } from '../../constant'
import { GridActionButton } from '../common/GridActionButton'
import { ColumnOptions, IColumnForOption } from './ColumnOptions'
import { CustomDataGridWithPagination } from './CustomDataGridWithPagination'
import { PaginationComponent, StyledDataGridPro } from './GenericTable.style'
import MobileCardList from './MobileCardList'
import { NextArrow, PreviousArrow } from './PaginationArrows'
import { CenteredSpinner } from '../Spinner'

export interface IToolbarOptions {
  label: string
  Icon?: React.ReactNode
  onClick?: () => void
  Component?: React.ReactNode
  showSeparator?: boolean
}

export const GRID_ROW_HEIGHT = 44
export const GRID_HEADER_HEIGHT = 56

export const gridHeightForRows = (rowCount: number, min = 2, max = 6) =>
  GRID_HEADER_HEIGHT + Math.min(Math.max(rowCount, min), max) * GRID_ROW_HEIGHT

export const gridFittedHeights = (
  rows: readonly any[] | undefined,
  getRowHeight: ((params: GridRowHeightParams) => GridRowHeightReturnValue) | undefined,
  min = 2,
  max = 6,
) => {
  const heightOf = (rowCount: number) => {
    let total = 0
    for (let i = 0; i < rowCount; i++) {
      const model = rows?.[i]
      const rowHeight =
        model && getRowHeight ? getRowHeight({ id: i, model, densityFactor: 1 } as GridRowHeightParams) : undefined
      total += typeof rowHeight === 'number' ? rowHeight : GRID_ROW_HEIGHT
    }
    return GRID_HEADER_HEIGHT + total
  }
  return { min: heightOf(Math.min(Math.max(rows?.length ?? 0, min), max)), max: heightOf(max) }
}

export enum ColumnOptionButtonType {
  GridAction = 'GridAction',
  StyledIconButton = 'StyledIconButton',
}

const VerticalSeparator = styled('hr')`
  width: 0;
  height: 49px;
  border: none;
  border-left: 1px solid ${props => props.theme.palette.secondaryMain[props.theme.palette.mode]};
  opacity: 1;
`

export const StyledIconButton = styled(IconButton)`
  color: ${props => props.theme.palette.primaryMain[props.theme.palette.mode]};
  &:hover {
    background-color: ${props => props.theme.palette.secondaryLight[props.theme.palette.mode]};
  }
  &:disabled {
    color: ${props => props.theme.palette.secondaryLight[props.theme.palette.mode]};
  }
  width: 32px;
  height: 32px;
`

export interface GenericTableProps {
  id: string
  data?: any[] //data
  columns: GridColumns //columns
  pageSize?: number //page size
  onDownload?: () => void
  defaultColumnOption?: IColumnForOption[] //should be column header names
  fixedColumns?: IColumnForOption[]
  actionColumns?: IColumnForOption[]
  enableColumnOptions?: boolean
  columnOptionButttonType?: ColumnOptionButtonType
  enablePagination?: boolean
  enableExport?: boolean
  enableToolbarFilter?: boolean
  enableToolbar?: boolean
  pageOptions?: number[]
  initialPage?: number
  enableServerPagination?: boolean //enable server-side pagination with queryUrl
  totalCount?: number //total count for server-side pagination
  onPageChange?: (event: any, page: number) => void //callback when page changes
  onPageSizeChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void //callback when page size changes
  enablePaginationButtons?: {
    showFirstButton?: boolean
    showLastButton?: boolean
  }
  queryUrl?: string
  queryParams?: object
  onCellClick?: GridEventListener<'cellClick'> | undefined
  onRowClick?: GridEventListener<'rowClick'> | undefined
  toolbarOptions?: IToolbarOptions[]
  toolbarFilter?: ReactElement
  rowColor?: string | ((params: { row: any }) => string)
  mobileViewCard?: (row: any) => ReactElement
  enableFilter?: boolean
  isDownloading?: boolean
  noRowsMessage?: string
  onSelectionModelChange?: ((selectionModel: GridSelectionModel, details: GridCallbackDetails<any>) => void) | undefined
  selectionModel?: GridInputSelectionModel | undefined
  onCellEditCommit?: GridEventListener<'cellEditCommit'> | undefined
  isCellEditable?: ((params: GridCellParams<any, any, any>) => boolean) | undefined
  checkboxSelection?: boolean
  /** When false, multiple rows can be selected (e.g. bulk actions). Default true for backward compatibility. */
  disableMultipleSelection?: boolean
  isRowSelectable?: (params: GridRowParams<any>) => boolean
  disableSelectionOnClick?: boolean
  sortModel?: GridSortModel | undefined
  columnVisibilityModel?: GridColumnVisibilityModel | undefined
  getRowHeight?: ((params: GridRowHeightParams) => GridRowHeightReturnValue) | undefined
  apiRef?: React.MutableRefObject<GridApiPro>
  loading?: boolean
  enableQuickFilter?: boolean
  getRowClassName?: (params: GridRowClassNameParams<any>) => string
  getCellClassName?: (params) => string
  getDetailPanelContent?: ((params: GridRowParams<any>) => React.ReactNode) | undefined
  getDetailPanelHeight?: ((params: GridRowParams<any>) => number | 'auto') | undefined
  onDetailPanelExpandedRowIdsChange?: ((ids: GridRowId[], details: GridCallbackDetails<any>) => void) | undefined
  detailPanelExpandedRowIds?: GridRowId[] | undefined
  generateQueryKey?: (page: number, pageSize: number, fields?: string) => QueryKeyT | undefined
  customStyles?: any
  hideDefaultFooterPagination?: boolean
  hideFooterSelectedRowCount?: boolean
  hideColumnHeaders?: boolean
  rowTopSpacing?: number
  rowBottomSpacing?: number
  className?: any
  height?: number | string
  autoHeight?: boolean
  rowsToFit?: { min?: number; max?: number; fill?: boolean }
  fitRowsHeight?: boolean
  getRowId?: (row: GridValidRowModel) => GridRowId
  editMode?: GridEditMode | undefined
  onRowEditStart?: GridEventListener<'rowEditStart'> | undefined
  onRowEditStop?: GridEventListener<'rowEditStop'> | undefined
  onEditRowsModelChange?: ((editRowsModel: GridEditRowsModel, details: GridCallbackDetails<any>) => void) | undefined
  isViewAllRecords?: boolean
  headerColor?: string
  isMinifiedColumnHeader?: boolean
  noDataComponent?: React.JSXElementConstructor<any> | undefined
  localeText?: any
  minRowHeight?: string
  contextType?: DialogContextType
  initialState?: GridInitialState
  enableUXFeatures?: boolean
  customColumnOptionOpen?: boolean
  customColumnOptionSetOpen?: React.Dispatch<React.SetStateAction<boolean>>
  maxHeight?: number | string
  headerHeight?: number
  enableRowReordering?: boolean
  onRowOrderChange?: (params: GridRowOrderChangeParams) => void
  showHeaderOnEmptyState?: boolean
  allowHorizontalScroll?: boolean
  sx?: SxProps<Theme>
  updateFields?: (fields: string) => void
   onCellEditStart ?: GridEventListener<'cellEditStart'> | undefined 
  toolBarStyles?: any
}

const GenericTable: React.FC<GenericTableProps> = ({
  id,
  data: d,
  columns: c,
  onDownload, //will be implement in future
  defaultColumnOption,
  fixedColumns = [] as IColumnForOption[],
  actionColumns = [] as IColumnForOption[],
  enableColumnOptions = false,
  columnOptionButttonType = ColumnOptionButtonType.StyledIconButton,
  enablePagination = false,
  enableExport = false,
  enableServerPagination = false,
  enableToolbar = false,
  enableToolbarFilter = false,
  queryUrl,
  queryParams,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSize: ps = 5,
  pageOptions = [5, 10, 20],
  initialPage = 0,
  toolbarOptions,
  toolbarFilter,
  rowColor,
  enableFilter = false,
  isDownloading,
  noRowsMessage,
  onSelectionModelChange,
  onRowClick,
  disableSelectionOnClick,
  disableMultipleSelection = true,
  getRowClassName = params => (params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd'),
  sortModel,
  getRowHeight = () => 35,
  loading = false,
  enableQuickFilter = false,
  getDetailPanelContent,
  onDetailPanelExpandedRowIdsChange,
  enablePaginationButtons: enablePaginationButton = { showFirstButton: true, showLastButton: true },
  generateQueryKey,
  customStyles,
  hideDefaultFooterPagination,
  hideFooterSelectedRowCount,
  hideColumnHeaders,
  rowTopSpacing = 0,
  rowBottomSpacing = 0,
  className,
  height,
  autoHeight = true,
  rowsToFit,
  fitRowsHeight = false,
  mobileViewCard,
  headerColor,
  isMinifiedColumnHeader = true,
  headerHeight,
  noDataComponent,
  contextType,
  customColumnOptionOpen,
  customColumnOptionSetOpen,
  columnVisibilityModel,
  maxHeight,
  getCellClassName,
  enableRowReordering = false,
  onRowOrderChange,
  showHeaderOnEmptyState = false,
  allowHorizontalScroll = false,
  sx,
  updateFields,
  toolBarStyles,
  checkboxSelection,
  ...props
}) => {
  const { t } = useTranslation(['common'])
  const initialRef: any = null
  const gridRef = useRef(initialRef)
  const [page, setPage] = useState(initialPage)
  const { getRowId } = props

  const rowColorSx = useMemo(() => {
    if (!rowColor) return {}
    if (typeof rowColor === 'string') {
      return {
        [`& .${gridClasses.row}`]: {
          '&.even, &.odd': { backgroundColor: rowColor },
        },
      }
    }
    if (typeof rowColor === 'function' && d) {
      const styles: Record<string, any> = {}
      d.forEach(row => {
        const color = rowColor({ row })
        if (color && color !== 'inherit') {
          const rowId = getRowId ? getRowId(row) : row.id
          styles[`& .${gridClasses.row}[data-id="${rowId}"]`] = {
            '&.even, &.odd': { backgroundColor: `${color} !important` },
          }
        }
      })
      return styles
    }
    return {}
  }, [rowColor, d, getRowId])
  const [pageSize, setPageSize] = useState(ps)
  const [data, setData] = useState(d)

  const fitted = useMemo(
    () => (rowsToFit ? gridFittedHeights(data, getRowHeight, rowsToFit.min, rowsToFit.max) : undefined),
    [rowsToFit, data, getRowHeight],
  )

  const resolvedHeight = fitted ? fitted.min : height
  const resolvedMinHeight = rowsToFit?.fill ? fitted?.min : undefined
  const resolvedMaxHeight = rowsToFit?.fill ? maxHeight : fitted?.max ?? maxHeight
  const resolvedFlex = rowsToFit?.fill ? '1 0 auto' : undefined
  const resolvedAutoHeight = rowsToFit ? false : autoHeight
  const [columns, setColumns] = useState(
    c.filter(
      column =>
        !fixedColumns?.some(c => c.field === column.field) && !actionColumns?.some(c => c.field === column.field),
    ),
  )
  const [fieldSet, setFieldSet] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [columnOptions, setColumnOptions] = useState(
    defaultColumnOption != undefined
      ? (defaultColumnOption.filter(
          dc => !fixedColumns?.some(c => c.field === dc.field) && !actionColumns?.some(c => c.field === dc.field),
        ) as IColumnForOption[])
      : [c[0].field],
  )
  const [columnArray, setColumnArray] = useState([] as IColumnForOption[])
  const theme = useTheme()
  const isSmall = useMediaQuery<any>(theme.breakpoints.down('sm'))

  useEffect(() => {
    const fieldSet = columns.map(c =>
      columnOptions.some(o => o.field === c.field)
        ? (c as any).fieldSet
          ? ((c as any).fieldSet as string)
          : (c.field as string)
        : null,
    )
    const filteredFieldSet = fieldSet.filter((f): f is string => f != null)
    setFieldSet([...filteredFieldSet])
    updateFields?.(filteredFieldSet.join(','))
  }, [columnOptions])

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    customColumnOptionSetOpen && customColumnOptionOpen ? customColumnOptionSetOpen(false) : setOpen(false)
  }

  const handlePageChange = useCallback(
    (event, page) => {
      setPage(page)
      onPageChange?.(event, page)
    },
    [onPageChange],
  )

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newPageSize = parseInt(event.target.value, 10)
      setPageSize(newPageSize)
      setPage(0)
      onPageSizeChange?.(event)
    },
    [onPageSizeChange],
  )

  useEffect(() => {
    fetchColumns()
  }, [d, c, ps, localStorage.getItem(`columnOptions-${id}`)])

  const fetchColumns = useCallback(async () => {
    setData(d)
    if (enableColumnOptions && enableToolbar  || (customColumnOptionOpen !== undefined && customColumnOptionSetOpen !== undefined)) {
      const op = await localStorage.getItem(`columnOptions-${id}`)
      if (op) {
        const storedOptions = JSON.parse(op)
        const currentAvailableColumns = c
          .filter(
            column =>
              !fixedColumns?.some(c => c.field === column.field) && !actionColumns?.some(c => c.field === column.field),
          )
          .map(col => ({ field: col.field, headerName: col.headerName }))
        const validStoredOptions = storedOptions.filter(option =>
          currentAvailableColumns.filter(c => c.field == option.field),
        )
        setColumns(
          c
            .filter(
              f =>
                validStoredOptions.some(o => o.field === f.field) &&
                !fixedColumns?.some(c => c.field === f.field) &&
                !actionColumns?.some(c => c.field === f.field),
            )
            .sort(
              (a, b) =>
                validStoredOptions.findIndex(c => c.field == a.field) -
                validStoredOptions.findIndex(c => c.field == b.field),
            ),
        )
        setColumnOptions(validStoredOptions)
        setColumnArray(
          currentAvailableColumns
            .filter(f => f.headerName && !validStoredOptions.some(o => o.field === f.field))
            .map(f => ({ field: f.field, headerName: f.headerName as string })),
        )
      } else {
        setColumns(
          c
            .filter(
              f =>
                columnOptions.some(o => o.field === f.field) &&
                !fixedColumns?.some(c => c.field === f.field) &&
                !actionColumns?.some(c => c.field === f.field),
            )
            .sort(
              (a, b) =>
                columnOptions.findIndex(c => c.field == a.field) - columnOptions.findIndex(c => c.field == b.field),
            ),
        )
        setColumnArray(
          c
            .filter(
              column =>
                !fixedColumns?.some(c => c.field === column.field) &&
                !actionColumns?.some(c => c.field === column.field),
            )
            .map(f => ({ field: f.field, headerName: f.headerName as string }))
            .filter(f => !columnOptions.some(o => o.field === f.field)) as IColumnForOption[],
        )
      }
    } else {
      const cols = localStorage.getItem(`columnOptions-${id}`)
      setColumns(cols ? JSON.parse(cols) : c)
    }
    setPageSize(ps)
  }, [d, c, ps, localStorage.getItem(`columnOptions-${id}`)])

  const handleRowOrderChange = useCallback(
    (params: GridRowOrderChangeParams) => {
      if (onRowOrderChange) {
        onRowOrderChange(params)
      }
    },
    [onRowOrderChange],
  )

  const handleColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      const adjustedOldIndex = checkboxSelection ? params.oldIndex - 1 : params.oldIndex
      const adjustedTargetIndex = checkboxSelection ? params.targetIndex - 1 : params.targetIndex

      if (adjustedOldIndex < 0 || adjustedTargetIndex < 0) return
      try {
        const stored = localStorage.getItem(`columnOptions-${id}`)
        const opts: IColumnForOption[] = stored
          ? JSON.parse(stored)
          : defaultColumnOption && defaultColumnOption?.length > 0
          ? (defaultColumnOption as IColumnForOption[])
          : (c as IColumnForOption[])

        if (adjustedOldIndex >= opts.length || adjustedTargetIndex >= opts.length) return
        const updated = [...opts]
        const [moved] = updated.splice(adjustedOldIndex, 1)

        updated.splice(adjustedTargetIndex, 0, moved)
        localStorage.setItem(`columnOptions-${id}`, JSON.stringify(updated))
      } catch (e) {
        return
      }
    },
    [fixedColumns.length, id, defaultColumnOption, checkboxSelection],
  )

  return (
    <>
      {enableToolbar &&
        (enableToolbarFilter ||
          Boolean(toolbarOptions?.length && toolbarOptions?.length > 0) ||
          enableColumnOptions ||
          enableExport) && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {enableToolbarFilter ? <Box key="toolbarFilter">{toolbarFilter}</Box> : <Box></Box>}
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="flex-end"
              spacing={1}
              py={1}
              sx={{ ...toolBarStyles }}
            >
              {toolbarOptions?.map((option, index) => {
                if (option.Icon) {
                  return (
                    <Tooltip title={option.label} key={index}>
                      <StyledIconButton onClick={option.onClick}>{option.Icon}</StyledIconButton>
                    </Tooltip>
                  )
                } else {
                  return (
                    <Fragment key={index}>
                      <Tooltip title={option.label} key={index}>
                        <Box>{option.Component}</Box>
                      </Tooltip>
                      {option.showSeparator && (
                        <Box>
                          <VerticalSeparator />
                        </Box>
                      )}
                    </Fragment>
                  )
                }
              })}
              {enableColumnOptions &&
                (columnOptionButttonType === ColumnOptionButtonType.GridAction ? (
                  <Tooltip title={t('column-options', { ns: 'generic-table' }) ?? ''}>
                    <GridActionButton buttonIcon={<Settings />} onClick={handleOpen} width="36px" height="36px" />
                  </Tooltip>
                ) : (
                  <Tooltip title={t('column-options', { ns: 'generic-table' }) ?? ''}>
                    <StyledIconButton
                      onClick={() => {
                        handleOpen()
                      }}
                    >
                      <Settings />
                    </StyledIconButton>
                  </Tooltip>
                ))}
              {enableExport &&
                (columnOptionButttonType === ColumnOptionButtonType.GridAction ? (
                  <Tooltip title={t('export_excel', { ns: 'common' }) ?? ''}>
                    <GridActionButton
                      buttonIcon={isDownloading ? <Downloading /> : <Download />}
                      onClick={() => {
                        !isDownloading && onDownload && onDownload()
                      }}
                      width="36px"
                      height="36px"
                      disabled={isDownloading}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={t('export_excel', { ns: 'common' }) ?? ''}>
                    <StyledIconButton
                      onClick={() => {
                        !isDownloading && onDownload && onDownload()
                      }}
                    >
                      {!isDownloading ? <Download /> : <Downloading />}
                    </StyledIconButton>
                  </Tooltip>
                ))}
            </Stack>
          </Stack>
        )}
      {!enableServerPagination ? (
        <>
          {isSmall ? (
            <Box
              sx={{
                height: resolvedHeight,
                minHeight: resolvedMinHeight,
                maxHeight: rowsToFit?.fill ? undefined : fitted?.max,
                flex: resolvedFlex,
                overflowY: 'auto',
              }}
            >
              <MobileCardList
                getDetailPanelContent={getDetailPanelContent}
                columns={[
                  ...c.filter(col => fixedColumns.some(fc => fc.field === col.field)),
                  ...columns,
                  ...c.filter(col => actionColumns.some(ac => ac.field === col.field)),
                ]}
                rows={data ?? []}
                onRowClick={onRowClick}
                onCellClick={props.onCellClick}
                mobileViewCard={mobileViewCard}
                contextType={contextType}
                getRowId={props.getRowId}
              />
            </Box>
          ) : (
            <Box
              sx={{
                height: resolvedHeight,
                minHeight: resolvedMinHeight,
                maxHeight: resolvedMaxHeight,
                flex: resolvedFlex,
                width: '100%',
                overflow: allowHorizontalScroll ? 'auto !important' : 'hidden !important',
              }}
            >
              {loading && rowsToFit ? (
                <CenteredSpinner height="100%" />
              ) : data && data?.length == 0 && loading == false && !showHeaderOnEmptyState && noDataComponent ? (
                React.createElement(noDataComponent)
              ) : (
                <StyledDataGridPro
                  id="dataGrid"
                  ref={gridRef!}
                  columns={[
                    ...c.filter(col => fixedColumns.some(fc => fc.field === col.field)),
                    ...columns,
                    ...c.filter(col => actionColumns.some(ac => ac.field === col.field)),
                  ]}
                  getRowClassName={Params => (getRowClassName ? getRowClassName(Params) : 'bg-white ')}
                  getCellClassName={getCellClassName}
                  rows={data ?? []}
                  rowCount={totalCount ?? data?.length ?? 0}
                  autoHeight={resolvedAutoHeight}
                  disableColumnFilter={!enableFilter}
                  disableDensitySelector
                  disableMultipleColumnsSorting
                  disableColumnMenu
                  disableMultipleSelection={disableMultipleSelection}
                  loading={loading}
                  disableColumnSelector
                  disableColumnResize
                  hideFooter={!enablePagination}
                  pagination={enablePagination}
                  rowsPerPageOptions={pageOptions}
                  page={page}
                  pageSize={pageSize}
                  onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                  onRowClick={onRowClick}
                  getRowHeight={getRowHeight}
                  getRowSpacing={_ => {
                    return {
                      top: rowTopSpacing ?? 0,
                      bottom: rowBottomSpacing ?? 0,
                    }
                  }}
                  className={`${customStyles ?? ''} ${className ?? ''} scrollbar`}
                  components={{
                    Toolbar: GridToolbar,
                    NoResultsOverlay:
                      noDataComponent ??
                      (() => (
                        <Box className="font-semibold text-center text-l text-grey-600">
                          {noRowsMessage ?? t('noResultFound')}
                        </Box>
                      )),
                    NoRowsOverlay:
                      noDataComponent ??
                      (() => (
                        <Box
                          className="font-semibold text-center text-l text-grey-600"
                          display={'flex'}
                          flexDirection={'column'}
                          alignItems={'center'}
                          p={6}
                        >
                          {noRowsMessage ?? t('noResultFound')}
                        </Box>
                      )),
                    DetailPanelExpandIcon: ArrowRight,
                    DetailPanelCollapseIcon: ArrowDropDown,
                  }}
                  hideFooterPagination={hideDefaultFooterPagination}
                  hideFooterSelectedRowCount={hideFooterSelectedRowCount}
                  hideColumnHeaders={hideColumnHeaders}
                  componentsProps={{
                    pagination: {
                      count: totalCount ?? data?.length ?? 0,
                      page: page,
                      onPageChange: handlePageChange,
                      onRowsPerPageChange: handleChangeRowsPerPage,
                      rowsPerPage: pageSize,
                      rowsPerPageOptions: pageOptions,
                      showFirstButton: enablePaginationButton?.showFirstButton ?? false,
                      showLastButton: enablePaginationButton?.showLastButton ?? false,
                    },
                    toolbar: {
                      printOptions: { disableToolbarButton: true },
                      csvOptions: { disableToolbarButton: true },
                      showQuickFilter: enableQuickFilter,
                      quickFilterProps: { debounceMs: QuickFilterBounceTime },
                    },
                  }}
                  onSelectionModelChange={onSelectionModelChange}
                  disableSelectionOnClick={disableSelectionOnClick}
                  getDetailPanelContent={getDetailPanelContent}
                  onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
                  initialState={{
                    sorting: {
                      sortModel: sortModel,
                    },
                  }}
                  isMinifiedColumnHeader={isMinifiedColumnHeader}
                  headerHeight={headerHeight ?? (isMinifiedColumnHeader ? 35 : undefined)}
                  rowReordering={enableRowReordering}
                  onRowOrderChange={handleRowOrderChange}
                  sx={{ ...rowColorSx, ...((sx as any) ?? {}) }}
                  allowHorizontalScroll={allowHorizontalScroll}
                  onColumnOrderChange={handleColumnOrderChange}
                  checkboxSelection={checkboxSelection}
                  {...props}
                  columnVisibilityModel={columnVisibilityModel}
                />
              )}
            </Box>
          )}
          {data?.length != 0 && enablePagination && (hideDefaultFooterPagination || isSmall) && (
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} padding={'8px'}>
              <PaginationComponent
                size="small"
                count={(data && Math.ceil(data?.length / pageSize)) ?? 0}
                page={page + 1}
                onChange={(event, page) => handlePageChange(event, page - 1)}
                renderItem={item => (
                  <PaginationItem components={{ next: NextArrow, previous: PreviousArrow }} {...item} />
                )}
              />
            </Box>
          )}
        </>
      ) : (
        <CustomDataGridWithPagination
          queryUrl={queryUrl as string}
          generateQueryKey={generateQueryKey}
          queryParams={queryParams}
          enablePagination={enablePagination}
          columns={[
            ...c.filter(col => fixedColumns.some(fc => fc.field === col.field)),
            ...columns,
            ...c.filter(col => actionColumns.some(ac => ac.field === col.field)),
          ]}
          initialPage={initialPage}
          mobileViewCard={mobileViewCard}
          pageOptions={pageOptions}
          pageSize={pageSize}
          loading={loading}
          getRowClassName={getRowClassName}
          getCellClassName={getCellClassName}
          setPageSize={setPageSize}
          noRowsMessage={noRowsMessage}
          onSelectionModelChange={onSelectionModelChange}
          enableFilter={enableFilter}
          onRowClick={onRowClick}
          enableQuickFilter={enableQuickFilter}
          getDetailPanelContent={getDetailPanelContent}
          enablePaginationButtons={enablePaginationButton}
          onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
          getRowHeight={getRowHeight}
          hideDefaultFooterPagination={hideDefaultFooterPagination}
          hideFooterSelectedRowCount={hideFooterSelectedRowCount}
          hideColumnHeaders={hideColumnHeaders}
          customStyles={customStyles}
          rowTopSpacing={rowTopSpacing}
          rowBottomSpacing={rowBottomSpacing}
          disableSelectionOnClick={disableSelectionOnClick}
          className={className}
          height={resolvedHeight}
          maxHeight={maxHeight}
          columnOptions={fieldSet}
          autoHeight={resolvedAutoHeight}
          fitRowsHeight={fitRowsHeight}
          sortModel={sortModel}
          isMinifiedColumnHeader={isMinifiedColumnHeader}
          noDataComponent={noDataComponent}
          contextType={contextType}
          enableRowReordering={enableRowReordering}
          onRowOrderChange={handleRowOrderChange}
          onColumnOrderChange={handleColumnOrderChange}
          showHeaderOnEmptyState={showHeaderOnEmptyState}
          allowHorizontalScroll={allowHorizontalScroll}
          {...props}
        />
      )}
      {enableColumnOptions && (
        <ColumnOptions
          id={id}
          open={customColumnOptionOpen ?? open}
          handleClose={handleClose}
          columns={c}
          fixedColumns={fixedColumns}
          actionColumns={actionColumns}
          columnOptions={columnOptions as IColumnForOption[]}
          setColumnOptions={setColumnOptions}
          columnArray={columnArray}
          setColumnArray={setColumnArray}
          setColumns={setColumns}
        />
      )}
    </>
  )
}

export default GenericTable
